import * as SelectPrimitive from "@radix-ui/react-select";
import { cva, VariantProps } from "class-variance-authority";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";

import { cn } from "./cn";

/**
 * 경계선은 border-input — Input·Textarea·Toggle(outline) 과 같은 "입력 요소의
 * 테두리" 역할. 이전엔 혼자 border-muted 를 써서 폼 컨트롤끼리 톤이 어긋났다.
 *
 * 사이즈 스케일은 Button·Toggle 과 맞춘다(default=md / sm=sm / lg=lg). 이전엔
 * 한 단계씩 작아서(default=sm / sm=xs) 같은 size="sm" 인데도 나란히 둔 버튼보다
 * 글씨가 작았다(12.8px vs 11.2px).
 *
 * 높이는 min-h-* — Button 이 min-h 라서, h 로 고정하면 라벨이 줄바꿈될 때
 * 한 행에서 높이가 어긋난다. 세로 패딩도 사이즈별로 Button 과 맞춰야 한다
 * (base 에 py-2 를 고정하면 sm 에서 내용 높이가 36px 을 넘어 버튼보다 1px
 * 커진다 — Button sm 은 py-1).
 */
const selectTriggerVariants = cva(
  "flex w-full items-center gap-x-1 justify-between rounded-md border border-input bg-background px-3 ring-offset-background text-foreground phone:text-lg focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive [&>span]:line-clamp-1",
  {
    variants: {
      size: {
        default: "min-h-10 py-2 text-md",
        sm: "min-h-9 py-1 text-sm",
        // 32px — 표 칸·좁은 줄 안 선택칸(Input xs 와 같은 높이·여백).
        xs: "min-h-8 px-2 py-1 text-sm",
        lg: "min-h-11 py-2 text-lg",
      },
      // ghost — 테두리·배경 없이 hover 에만 테두리(표 칸 안 공간·단위 선택 · 헤더 테마 선택) · size 뒤에 둬 세로 여백을 덮는다.
      variant: {
        default: "",
        ghost: "border-transparent bg-transparent px-1.5 py-0 hover:border-border",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  }
);
type SelectTriggerType = typeof SelectPrimitive.Trigger;

interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<SelectTriggerType> {
  size?: VariantProps<typeof selectTriggerVariants>["size"]; // 'size'가 올바르게 추론되는지 확인
  variant?: VariantProps<typeof selectTriggerVariants>["variant"];
  isArrowIcon?: boolean;
}

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<SelectTriggerType>,
  SelectTriggerProps
>(({ className, children, size, variant, isArrowIcon = true, ...props }, ref) => {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(selectTriggerVariants({ size, variant, className }))}
      {...props}>
      {children}
      {isArrowIcon && (
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 text-current" />
        </SelectPrimitive.Icon>
      )}
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}>
    <ChevronUp className="size-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}>
    <ChevronDown className="size-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

type OptionNavKey = "ArrowUp" | "ArrowDown" | "Home" | "End";

const isOptionNavKey = (key: string): key is OptionNavKey =>
  key === "ArrowUp" || key === "ArrowDown" || key === "Home" || key === "End";

// 열린 목록의 다음 강조 칸 — Radix 와 같은 규칙(끝에서 멈춤, 항목 밖에서 ↓ 는 첫 칸·↑ 는 끝 칸).
export const moveOptionIndex = (count: number, current: number, key: OptionNavKey): number | undefined => {
  if (count === 0) return undefined;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (current < 0) return key === "ArrowDown" ? 0 : count - 1;
  const next = key === "ArrowDown" ? current + 1 : current - 1;
  return next >= 0 && next < count ? next : undefined;
};

// Radix 는 ↑↓ 포커스를 setTimeout 뒤로 미뤄, 메인 스레드가 바쁘거나 타이머가 늦으면 다음 Enter 가 옛 칸을 고른다 — 같은 틱에 옮기고 Radix 처리는 건너뛴다.
const moveOptionFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (!isOptionNavKey(event.key)) return;
  const items = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>('[role="option"]:not([data-disabled])')
  );
  const next = moveOptionIndex(items.length, items.indexOf(event.target as HTMLElement), event.key);
  event.preventDefault();
  const node = next === undefined ? undefined : items[next];
  if (!node) return;
  node.focus({ preventScroll: true });
  node.scrollIntoView({ block: "nearest" });
  const viewport = event.currentTarget.querySelector<HTMLElement>("[data-radix-select-viewport]");
  if (viewport && next === 0) viewport.scrollTop = 0;
  if (viewport && next === items.length - 1) viewport.scrollTop = viewport.scrollHeight;
};

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", onKeyDown, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-96 min-w-32 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (!event.defaultPrevented) moveOptionFocus(event);
      }}>
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}>
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-7 pr-2.5 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-7 pr-2.5 text-sm outline-hidden data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      // 커서(키보드·호버)는 메뉴와 같은 옅은 코발트 — 코발트를 꽉 채우지 않는다.
      "focus:bg-accent focus:text-accent-foreground",
      // 선택 항목도 옅은 코발트에 굵기·체크로 가른다. `[data-state]:focus` 가 특이도가 높아 포커스가 얹혀도 유지된다.
      "data-[state=checked]:bg-accent data-[state=checked]:font-medium data-[state=checked]:text-accent-foreground",
      "data-[state=checked]:focus:bg-accent data-[state=checked]:focus:text-accent-foreground",
      className
    )}
    {...props}>
    {/* 체크 슬롯은 아이콘과 같은 16px — 이전엔 14px 박스에 16px 아이콘이라 넘쳤다 */}
    <span className="absolute left-1.5 flex size-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// Radix 항목은 빈 문자열 값을 못 가져 "" 는 이 표식으로 바꿔 들고, 밖으로는 다시 "" 로 돌려준다.
const EMPTY_VALUE = "__tk_empty__";
const toItemValue = (value: string): string => (value === "" ? EMPTY_VALUE : value);
const fromItemValue = (value: string): string => (value === EMPTY_VALUE ? "" : value);

export interface SimpleSelectOption {
  value: string;
  label: React.ReactNode;
}

type SimpleSelectKey = "ArrowUp" | "ArrowDown";

// 닫힌 칸에서 ↑↓ 를 native select 처럼 이전·다음 값으로 — 끝에서는 멈추고(undefined), 목록 밖 값은 첫 항목에서 센다.
export const stepOptionValue = (
  options: SimpleSelectOption[],
  current: string | undefined,
  key: SimpleSelectKey
): string | undefined => {
  const at = Math.max(0, options.findIndex((o) => o.value === current));
  const next = options[key === "ArrowDown" ? at + 1 : at - 1];
  return next?.value;
};

interface GuardEvent {
  preventDefault: () => void;
  currentTarget: { focus: () => void };
}

// 트리거에서 시작한 pointerdown 이 없는 click(라벨이 대신 보낸 것)은 포커스만 주고 막아 Radix 가 목록을 열지 않게 한다 — native select 와 같게.
export const createLabelClickGuard = () => {
  let pressed = false;
  return {
    onPointerDown: () => {
      pressed = true;
    },
    onPointerCancel: () => {
      pressed = false;
    },
    onClick: (event: GuardEvent) => {
      if (!pressed) {
        event.currentTarget.focus();
        event.preventDefault();
      }
      pressed = false;
    },
  };
};

interface SimpleSelectProps {
  options: SimpleSelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  name?: string;
  id?: string;
  "aria-label"?: string;
  // 오류 칸 — Input 과 같이 aria-invalid 만으로 빨간 테두리.
  "aria-invalid"?: boolean;
  disabled?: boolean;
  placeholder?: string;
  size?: SelectTriggerProps["size"];
  variant?: SelectTriggerProps["variant"];
  className?: string;
  // 표 칸처럼 닫힌 채 ↑↓ 로 값을 바로 바꿔야 하는 곳 — Space·Enter 로 여는 것은 그대로.
  arrowKeysChangeValue?: boolean;
}

const SimpleSelect = ({
  options,
  value,
  defaultValue,
  onValueChange,
  name,
  id,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
  disabled,
  placeholder,
  size,
  variant,
  className,
  arrowKeysChangeValue = false,
}: SimpleSelectProps) => {
  const [inner, setInner] = React.useState(defaultValue ?? "");
  const [guard] = React.useState(createLabelClickGuard);
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const wasOpen = React.useRef(false);
  // Radix 는 닫힌 뒤 setTimeout 에서야 트리거로 포커스를 돌려 그 사이 Tab 이 사이드바로 샌다 — 닫힘 커밋 직후 바로 돌린다.
  React.useEffect(() => {
    if (wasOpen.current && !open) triggerRef.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);
  const current = value ?? inner;
  // 목록에 없는 값은 native select 처럼 첫 항목으로 보이고 그 값이 제출된다.
  const selected = options.find((o) => o.value === current) ?? options[0];
  const shown = selected?.value;
  const commit = (plain: string) => {
    if (value === undefined) setInner(plain);
    onValueChange?.(plain);
  };
  return (
    <>
      {name && <input disabled={disabled} name={name} type="hidden" value={shown ?? ""} />}
      <Select
        disabled={disabled}
        open={open}
        value={shown === undefined ? "" : toItemValue(shown)}
        onOpenChange={setOpen}
        onValueChange={(next) => commit(fromItemValue(next))}>
        <SelectTrigger
          ref={triggerRef}
          aria-invalid={ariaInvalid || undefined}
          aria-label={ariaLabel}
          className={className}
          id={id}
          size={size}
          variant={variant}
          onClick={guard.onClick}
          onKeyDown={(event) => {
            if (!arrowKeysChangeValue || event.altKey || event.ctrlKey || event.metaKey) return;
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
            if (event.currentTarget.getAttribute("aria-expanded") === "true") return;
            event.preventDefault();
            const next = stepOptionValue(options, shown, event.key);
            if (next !== undefined) commit(next);
          }}
          onPointerCancel={guard.onPointerCancel}
          onPointerDown={guard.onPointerDown}>
          {/* 고른 이름을 직접 넣어 서버 렌더·하이드레이션 전에도 칸이 비지 않게 한다. */}
          <SelectValue placeholder={placeholder}>{selected?.label}</SelectValue>
        </SelectTrigger>
        {/* 포커스는 위 효과가 이미 돌렸다 — Radix 의 늦은 복귀가 그 사이 Tab 으로 옮긴 포커스를 되끌어오지 않게 막는다. */}
        <SelectContent onCloseAutoFocus={(event) => event.preventDefault()}>
          {options.map((option) => (
            <SelectItem key={option.value} value={toItemValue(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
};

export {
  SimpleSelect,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
